import { can, PortalPermissions } from "../../../../../../config/permissions";
import { LiveTableProps } from "../../../../types";
import useGetMe from "../../../../../../hooks/useCurrentUser";
import React, { useEffect, useState } from "react";
import {
  formatDateView,
  formatMoney,
} from "../../../../../adiqEdi/detalhesEdi/utils";
import LoadingOverlay from "../../../../../../components/LoadingOverlay";
import { Button, TableContainer } from "../../../../styles";
import Table, { TableColumn } from "../../../../../../components/Table";
import { Checkbox } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { formatProduct } from "../../../../utils";
import { Modal } from "../modal";
import { formatBillingType } from "../../../../../../utils/generalFormat";

export const LiveTable: React.FC<LiveTableProps> = ({
  data,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onSaveChanges = () => {},
}) => {
  const user = useGetMe();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalShowing, setIsModalShowing] = useState<boolean>(false);
  const [modalConfirmation, setModalConfirmation] = useState<boolean>(false);
  const formattedDate = (date: string) => formatDateView(date);
  const [modifiedPayments, setModifiedPayments] = useState<
    Record<
      number,
      { payed: boolean; merchantId?: string; totalPending?: number }
    >
  >({});
  const formattedProduct = (value: number) => formatProduct(value);

  useEffect(() => {
    setModifiedPayments({});
  }, [data.records]);

  const getRecordPayedStatus = (record: any) => {
    if (record.id in modifiedPayments) {
      return modifiedPayments[record.id];
    }
    return record.payed;
  };

  const changePayedSignatures = (
    e: CheckboxChangeEvent,
    record: {
      id: number;
      payed: boolean;
      merchantId: string;
      totalPending: number;
    }
  ) => {
    const isChecked = e.target.checked;

    setModifiedPayments((prev) => {
      const newState = { ...prev };

      if (isChecked === record.payed) {
        delete newState[record.id];
      } else {
        newState[record.id] = {
          payed: isChecked,
        };
        if (isChecked) {
          newState[record.id] = {
            payed: isChecked,
            totalPending: record.totalPending,
            merchantId: record.merchantId,
          };
        } else {
          delete newState[record.id];
        }
      }
      return newState;
    });
  };

  const changeAllPayedSignatures = (e: CheckboxChangeEvent) => {
    const isChecked = e.target.checked;

    setModifiedPayments((prev) => {
      const newState = { ...prev };

      data.records.forEach((record) => {
        if ((isChecked && !record.payed) || (!isChecked && record.payed)) {
          if (isChecked) {
            newState[record.id] = {
              payed: isChecked,
              totalPending: record.totalPending,
              merchantId: record.merchantId,
            };
          } else {
            delete newState[record.id];
          }
        } else {
          delete newState[record.id];
        }
      });

      return newState;
    });
  };

  const allChecked =
    data.records.length > 0 &&
    data.records.every((record) => getRecordPayedStatus(record));

  const someChecked = data.records.some((record) =>
    getRecordPayedStatus(record)
  );

  useEffect(() => {
    const handleSaveChanges = () => {
      const modifications = Object.entries(modifiedPayments).map(
        ([id, values]) => ({
          id: Number(id),
          values,
        })
      );

      onSaveChanges(modifications);
      setModifiedPayments({});
    };

    handleSaveChanges();
  }, [modalConfirmation]);

  const handleSaveClick = () => {
    setIsModalShowing(true);
  };

  const handleModalConfirmation = (confirmed: boolean) => {
    setIsModalShowing(false);

    if (confirmed) {
      const modifications = Object.entries(modifiedPayments).map(
        ([id, values]) => ({
          id: Number(id),
          values,
        })
      );

      onSaveChanges(modifications);
      setModifiedPayments({});
    }
  };

  return (
    <div>
      <LoadingOverlay show={isLoading} relative />

      <TableContainer>
        {user.userType === 3 && (
          <div style={{ marginBottom: 16, textAlign: "right" }}>
            {can(PortalPermissions.cobrancas.produtosContratados.concederDesconto) && (
              <Button
                type="primary"
                onClick={handleSaveClick}
                disabled={Object.keys(modifiedPayments).length === 0}
              >
                Salvar
              </Button>
            )}
            {Object.keys(modifiedPayments).length > 0 && (
              <span style={{ marginLeft: 8 }}>
                {Object.keys(modifiedPayments).length} alteração(ões)
                pendente(s)
              </span>
            )}
          </div>
        )}

        <Table
          dataSource={data.records}
          pagination={{
            defaultPageSize: pageSize,
            defaultCurrent: currentPage,
            current: currentPage,
            onShowSizeChange: (_, size) => {
              onPageSizeChange(size);
            },
            total: data.count,
            onChange: (pageNum) => onPageChange(pageNum),
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} assinaturas`,
          }}
        >
          <TableColumn
            title="Código do estabelecimento"
            dataIndex="merchantId"
            key="merchantId"
          />
          {user.userType === 3 && (
            <TableColumn
              title="Nome Fantasia"
              dataIndex="nickName"
              key="nickName"
            />
          )}
          <TableColumn
            title="Quantidade"
            dataIndex="posQuantity"
            key="posQuantity"
          />
          <TableColumn
            title="Produto"
            dataIndex="product"
            key="product"
            render={formatBillingType}
          />
          <TableColumn
            title="Valor por POS"
            dataIndex="posValue"
            key="posValue"
            render={formatMoney}
          />
          <TableColumn
            title="Valor Total"
            dataIndex="totalValue"
            key="totalValue"
            render={formatMoney}
          />
          {user.userType === 3 && (
            <TableColumn
              title="Valor Pendente"
              dataIndex="totalPending"
              key="totalPending"
              render={formatMoney}
            />
          )}
          <TableColumn
            title="Data da Última Cobrança"
            dataIndex="paymentDate"
            key="paymentDate"
            render={formattedDate}
          />

          {user.userType === 3 && (
            <TableColumn
              title={
                <div>
                  Pago
                  <Checkbox
                    style={{ paddingLeft: "8px" }}
                    name="payed"
                    onChange={changeAllPayedSignatures}
                    indeterminate={!allChecked && someChecked}
                    checked={allChecked}
                  ></Checkbox>
                </div>
              }
              render={(text: any, record: any) => (
                <Checkbox
                  checked={getRecordPayedStatus(record)}
                  name="payed"
                  onChange={(e) => changePayedSignatures(e, record)}
                />
              )}
            />
          )}
        </Table>
      </TableContainer>
      <Modal
        data={modifiedPayments}
        isShowing={isModalShowing}
        onConfirmation={handleModalConfirmation}
      />
    </div>
  );
};
